import { Table, Select, InputNumber, Button, Tag, message } from 'antd'
import { FilterOutlined, SendOutlined } from '@ant-design/icons'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'

function Accounts() {
  const { t } = useTranslation()
  const [accounts, setAccounts] = useState([])
  const [parentAccounts, setParentAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedKind, setSelectedKind] = useState(null)
  const [selectedParent, setSelectedParent] = useState(null)
  const [balMin, setBalMin] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])

  const token = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${token}` }

  const kindOptions = [
    { value: 1, label: t('accounts.kindCash') },
    { value: 2, label: t('accounts.kindOther') },
    { value: 3, label: t('accounts.kindSupplier') },
    { value: 4, label: t('accounts.kindDual') },
    { value: 5, label: t('accounts.kindRegularCustomer') },
    { value: 6, label: t('accounts.kindCustomerA') },
    { value: 7, label: t('accounts.kindCustomerB') },
    { value: 8, label: t('accounts.kindCustomerC') },
    { value: 9, label: t('accounts.kindEmployee') },
  ]

  useEffect(() => { fetchParentAccounts() }, [])

  const fetchParentAccounts = async () => {
    try {
      const res = await axios.get(
        '/api/settings/parent-accounts',
        { headers }
      )
      setParentAccounts(res.data)
    } catch (err) {
      message.error(t('accounts.fetchParentError'))
    }
  }

  const fetchAccounts = async () => {
    if (!selectedParent) {
      message.warning(t('accounts.chooseParentWarning'))
      return
    }
    setLoading(true)
    setSelectedRows([])
    try {
        const params = { parent_guid: selectedParent }
        if (selectedKind && selectedKind.length > 0) 
        params.kind = selectedKind.join(',')
        if (balMin !== null && balMin !== undefined) 
        params.bal_min = balMin
      const res = await axios.get(
        '/api/settings/accounts',
        { headers, params }
      )
      setAccounts(res.data)
    } catch (err) {
      message.error(t('accounts.fetchAccountsError'))
    }
    setLoading(false)
  }

  const columns = [
    {
      title: t('accounts.columnCode'),
      dataIndex: 'Code',
      key: 'Code',
      width: 110,
      render: code => <strong>{code}</strong>
    },
    {
      title: t('accounts.columnName'),
      dataIndex: 'Name',
      key: 'Name',
    },
    {
      title: t('accounts.columnParent'),
      dataIndex: 'parentAccName',
      key: 'parentAccName',
      render: name => <Tag color="purple">{name}</Tag>
    },
    {
      title: t('accounts.columnKind'),
      dataIndex: 'Kind',
      key: 'Kind',
      width: 130,
      render: kind => {
        const found = kindOptions.find(k => k.value === kind)
        const colors = {1:'gold',2:'default',3:'orange',4:'cyan',
                        5:'blue',6:'green',7:'lime',8:'volcano',9:'geekblue'}
        return <Tag color={colors[kind] || 'default'}>{found?.label || kind}</Tag>
      }
    },
    {
      title: t('accounts.columnBalance'),
      dataIndex: 'Bal',
      key: 'Bal',
      width: 130,
      align: 'center',
      render: v => (
        <span style={{
          color: v > 0 ? '#52c41a' : v < 0 ? '#ff4d4f' : '#888',
          fontWeight: 'bold',
          fontSize: 14
        }}>
          {v?.toLocaleString()}
        </span>
      ),
      sorter: (a, b) => a.Bal - b.Bal,
    },
    {
      title: t('accounts.columnMobile'),
      dataIndex: 'Mobile',
      key: 'Mobile',
      width: 130,
      render: mobile => mobile
        ? <a href={`tel:${mobile}`}>{mobile}</a>
        : <span style={{ color: '#ccc' }}>—</span>
    },
  ]

  const totalBal = accounts.reduce((s, r) => s + (r.Bal || 0), 0)

  return (
    <div>
      {/* فلاتر */}
      <div style={{
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: '16px',
        marginBottom: 16,
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <Select
          placeholder={t('accounts.parentPlaceholder')}
          style={{ width: 250 }}
          allowClear
          showSearch
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          onChange={setSelectedParent}
          options={parentAccounts.map(p => ({
            label: `${p.Code} - ${p.Name}`,
            value: p.GUID
          }))}
        />
        <Select
        placeholder={t('accounts.kindPlaceholder')}
        style={{ width: 200 }}
        mode="multiple"
        allowClear
        onChange={setSelectedKind}
        options={kindOptions}
        />        <InputNumber
          placeholder={t('accounts.balMinPlaceholder')}
          style={{ width: 160 }}
          onChange={setBalMin}
          min={0}
          formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        />
        <Button
          type="primary"
          icon={<FilterOutlined />}
          onClick={fetchAccounts}
          loading={loading}
        >
          {t('accounts.searchBtn')}
        </Button>
        {selectedRows.length > 0 && (
          <Button
            type="primary"
            icon={<SendOutlined />}
            style={{ background: '#25D366', borderColor: '#25D366' }}
          >
            {t('accounts.sendWhatsappBtn', { count: selectedRows.length })}
          </Button>
        )}
      </div>

      {/* الجدول */}
      <Table
        columns={columns}
        dataSource={accounts}
        loading={loading}
        rowKey="Code"
        rowSelection={{
          onChange: (_, rows) => setSelectedRows(rows),
          getCheckboxProps: record => ({
            disabled: !record.Mobile
          })
        }}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: total => t('accounts.totalSuffix', { count: total })
        }}
        summary={() => accounts.length > 0 && (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ background: '#fafafa' }}>
              <Table.Summary.Cell index={0} colSpan={4}>
                <strong>{t('accounts.totalRow', { count: accounts.length })}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="center">
                <strong style={{
                  color: totalBal > 0 ? '#52c41a' : totalBal < 0 ? '#ff4d4f' : '#888',
                  fontSize: 15
                }}>
                  {totalBal.toLocaleString()}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  )
}

export default Accounts
